/*
  # Intro
  When the customer uploads a document, we update the total page count based on the
  uploaded document. Currently, we only count pages in PDF documents; every other file
  type (DOCX, JPG, PNG, etc.) is counted as one page.

  # Why this file was rewritten (2025-11-03 → 2026-04)
  The original implementation counted PDF pages by matching `/Type /Page` with a regex
  on the PDF's raw binary content. That works for older/simpler PDFs, but fails on any
  PDF that uses compressed object streams (PDF 1.5+, the default for Word, modern
  Acrobat, Chrome "Print to PDF", etc.) — the page objects live inside a zlib-compressed
  blob that the regex can't see. Result: multi-page PDFs getting counted as 1 page,
  causing invoice disputes with business clients.

  The fix: parse the PDF properly using pdf-lib (https://pdf-lib.js.org/). This script
  loads pdf-lib dynamically from a CDN on first use — no theme.liquid change required.
  If the CDN is unreachable, we fall back to the original regex approach, so in the
  worst case we're no worse than before.

  # Basic flow
  FilePond fires events on the document whenever something interesting happens. This
  script listens for those events, counts the pages in added files, and updates the
  page count. When a file is removed, the count is recalculated from what remains.

  # Why a page count cache?
  The upload plugin keeps uploaded files across browser page reload (by storing key info
  in localStorage). When a file is first uploaded, `FilePond:processfile` gives us a
  Javascript File object we can read. After a reload, the same event fires again but
  the File object is gone. So we cache counts by serverId, which survives reloads.

  # Debugging
  Set `window.TolingoUploadDebug = true` in the browser console to enable verbose logs.
  Warnings (pdf-lib load failures, parse errors) are ALWAYS logged regardless.
*/
(async function () {
  const PDFLIB_CDN = "https://cdn.jsdelivr.net/npm/pdf-lib@1.17.1/dist/pdf-lib.min.js";

  function log() {
    if (window.TolingoUploadDebug) {
      console.log.apply(console, ["[upload]"].concat([].slice.call(arguments)));
    }
  }

  function warn() {
    console.warn.apply(console, ["[upload]"].concat([].slice.call(arguments)));
  }

  // -------------------------------------------------------------------------
  // pdf-lib dynamic loader
  // -------------------------------------------------------------------------
  let pdfLibPromise = null;

  function loadPdfLib() {
    if (typeof PDFLib !== "undefined" && PDFLib.PDFDocument) {
      return Promise.resolve(PDFLib);
    }
    if (pdfLibPromise) return pdfLibPromise;

    pdfLibPromise = new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = PDFLIB_CDN;
      script.async = true;
      script.onload = () => {
        if (typeof PDFLib !== "undefined" && PDFLib.PDFDocument) {
          log("pdf-lib loaded successfully");
          resolve(PDFLib);
        } else {
          warn("pdf-lib script loaded but PDFLib.PDFDocument is missing");
          resolve(null);
        }
      };
      script.onerror = (err) => {
        warn("Failed to load pdf-lib from CDN, falling back to regex. Error:", err);
        resolve(null);
      };
      document.head.appendChild(script);
    });

    return pdfLibPromise;
  }

  // Start loading immediately so it's likely ready by the time the first file uploads.
  loadPdfLib();

  // -------------------------------------------------------------------------
  // Page count cache (persisted in localStorage, keyed by file.serverId)
  // -------------------------------------------------------------------------
  const cache = {
    load() {
      const l = localStorage.getItem(this.localStorageKey);
      try {
        const parsed = JSON.parse(l);
        this.cache = typeof parsed === "object" && parsed !== null ? parsed : {};
      } catch (e) {
        log("cache error", e);
        this.cache = {};
      }
    },
    save() {
      localStorage.setItem(this.localStorageKey, JSON.stringify(this.cache));
    },
    setKey(key, value) {
      this.cache[key] = value;
      this.save();
    },
    getKey(key) {
      return this.cache[key];
    },
    hasKey(key) {
      return Object.prototype.hasOwnProperty.call(this.cache, key);
    },
    clearExcept(exceptKeys) {
      Object.keys(this.cache).forEach((key) => {
        if (!exceptKeys.includes(key)) {
          delete this.cache[key];
        }
      });
      this.save();
    },
    localStorageKey: "uploadPageCountCache",
    cache: {},
  };
  cache.load();

  // -------------------------------------------------------------------------
  // PDF page counting: pdf-lib primary, regex fallback
  // -------------------------------------------------------------------------
  async function countPdfPagesWithPdfLib(file, lib) {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await lib.PDFDocument.load(arrayBuffer, {
      ignoreEncryption: true,
      throwOnInvalidObject: false,
      updateMetadata: false,
    });
    return pdfDoc.getPageCount();
  }

  async function countPdfPagesWithRegex(file) {
    const arrayBuffer = await file.arrayBuffer();
    // Decode as latin1 so every byte becomes one character (preserves markers).
    const bytes = new Uint8Array(arrayBuffer);
    let content = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      content += String.fromCharCode.apply(
        null,
        bytes.subarray(i, Math.min(i + chunk, bytes.length))
      );
    }
    const matches = content.match(/\/Type[\s]*\/Page[^s]/g);
    return matches ? matches.length : 1;
  }

  async function countPdfPages(file) {
    const lib = await loadPdfLib();

    if (lib) {
      try {
        const count = await countPdfPagesWithPdfLib(file, lib);
        log("countPdfPages (pdf-lib)", file.name, count);
        return count;
      } catch (err) {
        warn("pdf-lib failed on", file.name, "- trying regex fallback.", err);
      }
    } else {
      warn("pdf-lib unavailable, using regex fallback for", file.name);
    }

    try {
      const count = await countPdfPagesWithRegex(file);
      log("countPdfPages (regex fallback)", file.name, count);
      return count;
    } catch (err) {
      warn("Regex fallback also failed on", file.name, "- returning 1.", err);
      return 1;
    }
  }

  async function analyzeFile(file) {
    log("analyzeFile", { name: file.name, type: file.type });
    if (file.type === "application/pdf") {
      return await countPdfPages(file);
    } else {
      // DOCX, JPG, PNG, etc. — count each file as 1 page.
      return 1;
    }
  }

  // -------------------------------------------------------------------------
  // File lifecycle
  // -------------------------------------------------------------------------
  async function addFile(detail) {
    log("addFile", { serverId: detail.file.serverId, name: detail.file.filename });

    if (!File.prototype.isPrototypeOf(detail.file.file)) {
      // After a reload, detail.file.file only contains a string (the serverId)
      // instead of the whole file object. We rely on the cached count in that case.
      log("addFile: no File object (likely post-reload), skipping analysis");
      return;
    }

    const cacheKey = detail.file.serverId;
    if (!cacheKey) {
      warn("addFile: missing serverId, skipping");
      return;
    }

    // Marker that this file is being analyzed. If the user removes the file
    // before analysis finishes, we check the cache before writing the result.
    cache.setKey(cacheKey, null);

    const pageCount = await analyzeFile(detail.file.file);
    log("addFile complete", { serverId: cacheKey, pageCount });

    if (cache.hasKey(cacheKey)) {
      cache.setKey(cacheKey, pageCount);
      updatePageCount(detail.pond.getFiles());
    }
  }

  function getPageCount(files) {
    const cacheKeys = files.map((f) => f.serverId);
    const pageCounts = cacheKeys.map((key) => cache.getKey(key) || 0);
    cache.clearExcept(cacheKeys);

    return pageCounts.reduce((acc, pageCount) => acc + pageCount, 0);
  }

  function updatePageCount(files) {
    const pageCount = getPageCount(files);
    log("updatePageCount", { fileCount: files.length, pageCount });

    const element = document.querySelector("quantity-input .quantity__input");
    if (!element) {
      warn("updatePageCount: quantity input not found");
      return;
    }

    element.value = pageCount || 1;
    element.dispatchEvent(new Event("change"));
  }

  // -------------------------------------------------------------------------
  // FilePond event listeners
  // -------------------------------------------------------------------------
  document.addEventListener("FilePond:init", (event) => {
    log("FilePond:init", { fileCount: event.detail.pond.getFiles().length });
    updatePageCount(event.detail.pond.getFiles());
  });

  // FilePond:processfile fires when the upload is complete (we have a serverId).
  document.addEventListener("FilePond:processfile", (event) => {
    log("FilePond:processfile", { serverId: event.detail.file.serverId });
    addFile(event.detail);
  });

  document.addEventListener("FilePond:removefile", (event) => {
    log("FilePond:removefile", { serverId: event.detail.file.serverId });
    updatePageCount(event.detail.pond.getFiles());
  });
})();